import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import ReadAloudButton from './ReadAloudButton';
import { brand } from '../../config/brand';

const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0, ml: 3, py: 0.5 }}>
    <ListItemText
      primary={children}
      primaryTypographyProps={{
        variant: 'body1',
        color: 'text.secondary',
        sx: { lineHeight: 1.8 },
      }}
    />
  </ListItem>
);

const SubBullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ListItem sx={{ display: 'list-item', listStyleType: 'circle', pl: 0, ml: 6, py: 0.3 }}>
    <ListItemText
      primary={children}
      primaryTypographyProps={{
        variant: 'body1',
        color: 'text.secondary',
        sx: { lineHeight: 1.8 },
      }}
    />
  </ListItem>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
    {children}
  </Typography>
);

const Paragraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 1.5 }}>
    {children}
  </Typography>
);

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const termsLink = (
    <RouterLink to="/terms" style={{ color: 'inherit' }}>
      Terms of Use
    </RouterLink>
  );

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: brand.gradients.hero,
          color: 'white',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              component="h1"
              fontWeight="bold"
              sx={{ fontSize: { xs: '2rem', md: '3rem' }, mb: 2 }}
            >
              Privacy Policy
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Last updated on 14/01/26
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper
          id="legal-content"
          sx={{
            p: { xs: 3, md: 5 },
            bgcolor:
              theme.palette.mode === 'light'
                ? 'rgba(255,255,255,0.85)'
                : 'rgba(30,30,40,0.85)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Box data-no-read sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <ReadAloudButton contentSelector="#legal-content" />
          </Box>

          {/* 1. INTRODUCTION */}
          <SectionTitle>1. INTRODUCTION</SectionTitle>
          <List disablePadding>
            <Bullet>
              {`At ${brand.legal.companyName}. under incorporation (Collectively and individually "\u2026.", "Company", "We," "Us," or "Our"), we respect your privacy and are committed to protecting your personal data.`}
            </Bullet>
            <Bullet>
              {`The purpose of this privacy policy (hereinafter: "the Policy") is to explain the Company\u2019s practices regarding the privacy of users of the website at ${brand.legal.website} (hereinafter: "the Website"), and the service we provide (hereinafter: "AI coding agent", and together: the "Service"), including how we collect personal data, the purposes for which we use it, and how and with whom we may share it.`}
            </Bullet>
            <Bullet>
              This Policy forms an integral part of our {termsLink}. By using the Service, you agree to the terms of this Policy. If you do not agree with any part of this Policy, please do not use the Service and/or the Website.
            </Bullet>
            <Bullet>
              The Company may update this Policy from time to time. Updates will become effective once posted on the Website. Your continued use of the Website or the Service after any changes means you accept the updated Policy.
            </Bullet>
            <Bullet>
              You agree that any dispute arising from your use of the Service will be governed exclusively by Israeli law and adjudicated solely in the competent courts of the Tel Aviv district.
            </Bullet>
            <Bullet>
              {'The term "you" as used in this Policy refers to all users of the Company\u2019s services, as well as the personal data and other information relating to those users.'}
            </Bullet>
            <Bullet>
              {'This Policy is governed by and interpreted in accordance with the provisions of the Israeli Privacy Protection Law, 5741-1981, and the regulations enacted thereunder (together, the "Privacy Protection Law"), as well as any other applicable privacy laws and regulations to the extent relevant to the Company\u2019s operations and your use of the Service (together: "Applicable Data Protection Law"). All capitalized terms not defined herein shall have the meaning ascribed to them under the Privacy Protection Law.'}
            </Bullet>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* 2. COLLECTION OF PERSONAL DATA */}
          <SectionTitle>2. COLLECTION OF PERSONAL DATA</SectionTitle>
          <List disablePadding>
            <Bullet>
              This Service does not collect your personal data, and you are not required to provide us with personal data. However, while using the Service, you may choose to provide us- voluntarily and with your consent- with personal data, for example, when contacting us or signing up for a service or Website mailing list.
            </Bullet>
            <Bullet>
              If you provide or upload any personal data to the Service, you represent that the personal data you provide and/or update is accurate and complete, and that you are providing the personal data for yourself or have legal authorization to provide it for another person. You are solely responsible for the personal data you provide.
            </Bullet>
            <Bullet>
              Some services on the Service, now or in the future, may require registration. As part of registration, you may be asked to provide personal data, such as your name, address, and contact details (telephone, email). Required fields will be clearly marked. If you do not provide the requested data in the marked fields, you may not be able to register for certain services.
            </Bullet>
            <Bullet>
              You are not required to provide us with your personal data, but in certain cases, if you do not provide the requested personal data, we may not be able to provide you with the services you request. For example, you may not be able to receive notifications, updates, or responses to inquiries, or register for services without providing certain personal data.
            </Bullet>
            <Bullet>
              When using the Service, we may collect personal data about you. This includes personal data you provide voluntarily, records of your customer service inquiries, statistical information that does not identify you, or data we automatically collect about your use of our Website. Types of personal data we collect may include your full name, email address, browser information, records of your inquiries, and more. Below is a detailed list of the types of personal data we collect and how we collect them:
            </Bullet>
            <SubBullet>
              Personal data you provide voluntarily: When you sign up for our Service, or for our newsletter, with your explicit consent - your full name, email address, and telephone number.
            </SubBullet>
            <SubBullet>
              Personal data collected automatically: IP address, browser type, Website usage data, pages you viewed, products of interest, device identifiers, general location, as well as personal data collected through cookies, Google Analytics, Meta Pixel, and similar tools.
            </SubBullet>
            <SubBullet>
              Statistical information: The Company may collect anonymous or aggregate information (such as device type, operating system, device language, click stream, browsing duration). This information does not personally identify you.
            </SubBullet>
            <SubBullet>
              Customer service inquiries: The Company may keep records of your inquiries (including email correspondence or WhatsApp messages) for handling and quality assurance purposes.
            </SubBullet>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* 3. USE OF PERSONAL DATA */}
          <SectionTitle>3. USE OF PERSONAL DATA</SectionTitle>
          <List disablePadding>
            <Bullet>
              We may use your personal data to contact you, to provide you with the Service, to deliver information and updates you sign up for, to send updates regarding your personal data stored in our systems and/or changes to this Policy, to improve our Service or our Website, to protect your safety and the safety of our users, to comply with legal obligations, and any other use necessary to provide you with our services in a secure and orderly manner.
            </Bullet>
            <Bullet>
              We may use your personal data, together with information from other customers, for statistical and analytical purposes to improve our services and streamline our operations, while protecting your privacy and minimizing the use of your personal details where possible.
            </Bullet>
            <Bullet>
              We may also use your personal data to prevent fraud or suspicious activity, maintain the security of our systems and users, protect our rights and legitimate business interests, and comply with our legal and regulatory obligations, including responding to court orders and requests from authorities as required by any Applicable Data Protection Laws.
            </Bullet>
            <Bullet>
              We may use your contact information to send you marketing communications and service messages, as described in Section 6 of this Policy.
            </Bullet>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* 4. SHARING PERSONAL DATA WITH THIRD PARTIES */}
          <SectionTitle>4. SHARING PERSONAL DATA WITH THIRD PARTIES</SectionTitle>
          <List disablePadding>
            <Bullet>
              The Company will not share your personal data with third parties except in the following cases:
            </Bullet>
            <SubBullet>
              With technology service providers (such as Google Analytics, cloud providers, mailing systems) or if required by Applicable Data Protection Laws, court order, or legal process.
            </SubBullet>
            <SubBullet>
              To protect the rights, property, or safety of the Company or third parties.
            </SubBullet>
            <SubBullet>
              {'In connection with a merger, acquisition, or sale of all or part of the Company\u2019s assets, provided that the third party agrees to protect your personal data consistent with this Policy.'}
            </SubBullet>
            <SubBullet>
              We may share aggregated or de-identified information with third parties for analytics, research or service improvement purposes. Aggregated or de-identified information does not identify individual users and is not considered personal data.
            </SubBullet>
            <Bullet>
              {'Some of the Company\u2019s service providers, including providers of cloud storage, analytics, or AI tools, may be located outside of Israel. In such cases, the Company takes steps to ensure that your personal data is adequately protected, including by requiring that recipients maintain appropriate security and confidentiality measures and comply with Applicable Data Protection Laws.'}
            </Bullet>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* 5. USE OF COOKIES, ANALYTICS TOOLS AND CHATBOTS */}
          <SectionTitle>5. USE OF COOKIES, ANALYTICS TOOLS AND CHATBOTS</SectionTitle>
          <List disablePadding>
            <Bullet>
              The Website uses cookies and similar technologies (such as pixels and web beacons) for proper operation, improving user experience, statistical analysis, content and advertising personalization, and fraud prevention.
            </Bullet>
            <Bullet>
              Types of cookies:
            </Bullet>
            <SubBullet>
              {'Essential cookies \u2013 required for basic operation of the Website.'}
            </SubBullet>
            <SubBullet>
              {'Functional cookies \u2013 to improve user experience (such as saving preferences).'}
            </SubBullet>
            <SubBullet>
              {'Statistical cookies \u2013 to collect anonymous usage data.'}
            </SubBullet>
            <SubBullet>
              {'Advertising cookies \u2013 to display personalized advertisements.'}
            </SubBullet>
            <Bullet>
              Third-party cookies: The Website uses third-party tools (such as Google Analytics, Meta Pixel) that may place cookies on your device.
            </Bullet>
            <Bullet>
              You can block or delete cookies through your browser settings; however, some Website services may not be available to you if you do so.
            </Bullet>
            <Bullet>
              By using the Website, you consent to the use of cookies as described above.
            </Bullet>
            <Bullet>
              Please note that advertisements and marketing offers on the Website may be provided by third parties authorized by us. These third parties may use cookies that are not controlled by us, and their use is subject to their own privacy policies.
            </Bullet>
            <Bullet>
              Automated tools, including chatbots, may be used to provide initial responses to inquiries and improve service. You can request to speak with a human representative at any time. The Company will not make automated decisions affecting your legal rights or any other similar rights solely based on automatic tools, without human involvement. Where automated tools are used to support decision-making, meaningful human review and oversight are always maintained.
            </Bullet>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* 6. MARKETING COMMUNICATIONS AND SERVICE MESSAGES */}
          <SectionTitle>6. MARKETING COMMUNICATIONS AND SERVICE MESSAGES</SectionTitle>
          <List disablePadding>
            <Bullet>
              We will only send you newsletters or marketing communications with your express consent, in accordance with Section 30A of the Communications (Telecommunications and Broadcasting) Law, 5742-1982.
            </Bullet>
            <Bullet>
              Marketing communications may be sent by email, WhatsApp, SMS, or other digital means.
            </Bullet>
            <Bullet>
              You may opt out of receiving marketing communications at any time by using the unsubscribe link in our messages or by contacting the Company directly.
            </Bullet>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* 7. MINORS */}
          <SectionTitle>7. MINORS</SectionTitle>
          <Paragraph>
            {'The services on this Website are intended for individuals 18 years of age or older. We do not knowingly collect personal data from users under 18. If you are under 18, you must have your parent\u2019s or legal guardian\u2019s permission to use the services and provide personal data. If we learn that we have collected personal data from a minor without proper consent, we will delete that information as soon as possible.'}
          </Paragraph>

          <Divider sx={{ my: 3 }} />

          {/* 8. DATA RETENTION AND DATA SECURITY */}
          <SectionTitle>8. DATA RETENTION AND DATA SECURITY</SectionTitle>
          <List disablePadding>
            <Bullet>
              Personal data will be stored in the Company&apos;s databases only for as long as necessary to fulfill the purposes for which it was collected, such as account management, providing the Service, to resolve disputes or as required by law.
            </Bullet>
            <Bullet>
              Instead of deleting personal data, the Company may de-identify or aggregate it so that it no longer identifies you.
            </Bullet>
            <Bullet>
              You may contact the Company with requests related to your personal data, in accordance with legal provisions and subject to Section 12 below.
            </Bullet>
            <Bullet>
              The Company takes reasonable and industry-standard security measures to protect personal data, including encryption, access controls, and system monitoring. However, remember that no method of transmission or storage is completely secure. Therefore, the Company is not responsible for unauthorized access or use of your personal data if it has taken all reasonable and legally required measures and the breach was caused by circumstances beyond its control.
            </Bullet>
            <Bullet>
              When transferring personal data to third parties as described in Section 4, the Company uses vendors who maintain high standards of security and privacy protection. Only the personal data necessary to perform the requested services is shared with these external suppliers, subject to their agreement to maintain confidentiality and comply with our legal obligations.
            </Bullet>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* 9. USE OF AI TOOLS */}
          <SectionTitle>9. USE OF AI TOOLS</SectionTitle>
          <List disablePadding>
            <Bullet>
              {'The Company reserves the right to employ advanced technological means, including the use of business intelligence tools, algorithm-based systems, advanced data analysis systems, generative artificial intelligence and machine learning systems, and systems performing automated actions based on artificial intelligence ("AI Tools"), for the purpose of operating, maintaining, and improving the Service. Such uses may include, among others, generating, optimizing, and testing code, enhancing system performance, improving user experience, and supporting other legitimate business purposes of the Company, all as detailed below. It is hereby clarified that any use of such technologies shall be subject to this Privacy Policy and to the provisions of any Applicable Data Protection Laws.'}
            </Bullet>
            <Bullet>
              {'Further to the above, and with respect to the use of data within artificial intelligence\u2013based systems, it is clarified that these AI Tools rely on models developed by third parties under licenses acquired by the Company for its code generation platform, and are used in accordance with the terms of such licenses.'}
            </Bullet>
            <Bullet>
              {'The Company undertakes to use AI Tools in a safe and responsible manner, adhering to recognized ethical standards in the industry, applicable international principles, and all relevant laws and regulations, including any Applicable Data Protection Laws as amended from time to time, and always in alignment with the Company\u2019s legitimate purposes related to the operation and improvement of its code generation services.'}
            </Bullet>
            <Bullet>
              {'The Company\u2019s use of AI Tools includes, among others, analyzing, generating, optimizing, and testing code, improving the accuracy and efficiency of the Services, providing automated coding suggestions, analyzing usage patterns to enhance user experience, and supporting other purposes described in these Terms.'}
            </Bullet>
            <Bullet>
              The Company ensures that all uses of AI-based systems and applications are carried out with discretion and caution, following a risk assessment process, implementing dedicated internal controls, and applying industry-standard security measures to protect data and ensure responsible AI deployment.
            </Bullet>
            <Bullet>
              The AI Tools operated by the Company serve as supportive tools in the code generation and optimization processes and do not replace human judgment. The Company ensures meaningful human oversight in all decision-making processes, particularly those that may materially affect users or the integrity of generated code. The Company does not guarantee absolute accuracy or completeness of AI-generated outputs and continuously works to improve system performance and minimize potential errors.
            </Bullet>
            <Bullet>
              The Service utilizes AI algorithms that may process large volumes of data to generate code and recommendations. The Company implements technical and organizational measures designed to prevent the collection of personal data; however, due to the nature of AI systems and technical limitations, there is a possibility that personal data may be inadvertently processed. The Company shall not be liable for any such inadvertent processing, provided that reasonable measures have been taken to minimize such risk. For more information, please refer to our Privacy Policy.
            </Bullet>
            <Bullet>
              {'The scope of data that the AI Tools may access and process is determined by the user, who defines the amount and type of information provided to the platform. While the Company implements technical and organizational measures designed to prevent the collection and processing of personal data, if a user allows the platform to access data that includes personal data, such data may be inadvertently processed by the AI Tools. Users are responsible for ensuring that any data they permit the platform to access complies with Applicable Data Protection Laws and does not unlawfully include personal or Sensitive Data (as described below). The Company cannot guarantee that personal data will not be processed if it is included in the data defined by the user, despite the Company\u2019s best efforts to minimize such risks.'}
            </Bullet>
          </List>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 1.5, ml: 3 }}>
            {'For the purposes of this Policy, "Sensitive Data" includes information such as national identification numbers, health or medical information, biometric data, financial account details, and any other data classified as sensitive under Applicable Data Protection Laws.'}
          </Typography>
          <List disablePadding>
            <Bullet>
              {'If the Company uses data of a particularly sensitive nature, as defined under Applicable Data Protection Laws, within its AI tools, it will apply enhanced safeguards to ensure safe processing and transparency. These measures include conducting heightened risk assessments, minimizing data collection and usage, implementing robust security controls, maintaining human oversight, and providing users with the right to object to the use of their data in such tools where there is a significant risk of harm to their rights, to the extent technically feasible.'}
            </Bullet>
            <Bullet>
              Without derogating from the above, users are solely responsible for ensuring that any data they upload, provide, or grant the platform access to, does not infringe on the rights of others or violate any Applicable Data Protection Laws. Please do not provide or allow access to personal or sensitive data unless you have the necessary rights and permissions to do so.
            </Bullet>
            <Bullet>
              {'For additional information regarding how decisions are made using AI Tools within the code generation platform, or to obtain an explanation of an algorithmic decision, users may contact the Company\u2019s Data Protection Officer, at the email address as provided under Section 13 of this Policy.'}
            </Bullet>
            <Bullet>
              {'The Company\u2019s artificial intelligence policy will be reviewed and updated periodically to reflect technological, regulatory, and industry developments relevant to the responsible and ethical use of AI in code generation.'}
            </Bullet>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* 10. DATABASES */}
          <SectionTitle>10. DATABASES</SectionTitle>
          <Paragraph>
            {'Personal data collected will be stored in the Company\u2019s databases and under its responsibility, subject to the Privacy Protection Law, 5741-1981, and the regulations enacted thereunder, including the Privacy Protection Regulations (Data Security), 5777-2017.'}
          </Paragraph>

          <Divider sx={{ my: 3 }} />

          {/* 11. Links to External Websites */}
          <SectionTitle>11. Links to External Websites</SectionTitle>
          <Paragraph>
            The Website may include links to external websites or services (such as WhatsApp). Your use of these services is subject to the privacy policies of those third parties, and the Company is not responsible for their practices.
          </Paragraph>

          <Divider sx={{ my: 3 }} />

          {/* 12. DATA SUBJECT RIGHTS */}
          <SectionTitle>12. DATA SUBJECT RIGHTS</SectionTitle>
          <Paragraph>
            In accordance with the Privacy Protection Law:
          </Paragraph>
          <List disablePadding>
            <Bullet>
              You have the right to request access to the personal data we hold about you.
            </Bullet>
            <Bullet>
              You have the right to request correction or deletion of your personal data if it is inaccurate, incomplete, or outdated.
            </Bullet>
            <Bullet>
              {`Requests should be directed to: ${brand.contactEmail}. The Company will respond to your request as soon as possible and no later than 30 days from the date of receipt, in accordance with legal requirements.`}
            </Bullet>
            <Bullet>
              Under certain circumstances and jurisdictions, you may have the right to request the deletion of all your personal data. For more information please contact us at the email provided under Section 13.
            </Bullet>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* 13. CONTACT US */}
          <SectionTitle>13. CONTACT US</SectionTitle>
          <Paragraph>
            {`For questions or requests regarding your Personal Data or this Policy, you can contact us at: Email: ${brand.contactEmail}`}
          </Paragraph>

          {/* Back to Home */}
          <Box data-no-read sx={{ mt: 5, textAlign: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ fontWeight: 600 }}
            >
              Back to Home
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPage;
